import { useT } from '../../lib/i18n';
import { ScreenHeading } from '../shell/PortalShell';
import type { PortalBranch } from '../../lib/types';

/**
 * Location picker. Skipped automatically by the flow when a tenant publishes
 * only one branch — asking someone to choose between one option is noise.
 */
export function BranchStep({
  branches,
  selectedId,
  onSelect,
}: {
  branches: PortalBranch[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}): JSX.Element {
  const { t, lang } = useT();

  return (
    <div className="bk-rise">
      <ScreenHeading title={t('choose_location')} subtitle={t('choose_location_sub')} />
      <div className="grid gap-3 sm:grid-cols-2">
        {branches.map((branch) => {
          const name = lang === 'ar' && branch.name_ar ? branch.name_ar : branch.name;
          const address = lang === 'ar' && branch.address_ar ? branch.address_ar : branch.address;
          return (
            <button
              key={branch.id}
              type="button"
              className="bk-tile bk-media-tile"
              data-selected={selectedId === branch.id}
              onClick={() => onSelect(branch.id)}
            >
              {branch.image && (
                <img src={branch.image} alt="" className="h-28 w-full object-cover sm:h-32" loading="lazy" />
              )}
              <span className="block p-4">
                <span className="block text-sm font-semibold text-zinc-900">{name}</span>
                {address && <span className="mt-0.5 block text-xs text-zinc-500">{address}</span>}
                {branch.latitude != null && branch.longitude != null && (
                  <span
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium"
                    style={{ color: 'var(--accent)' }}
                    onClick={(e) => {
                      // Opening the map must not also select the branch.
                      e.stopPropagation();
                      window.open(
                        `https://maps.google.com/?q=${branch.latitude},${branch.longitude}`,
                        '_blank',
                        'noopener',
                      );
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1118 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {lang === 'ar' ? 'الخريطة' : 'Map'}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
